import { IImageProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPSpinner } from 'baseComponents';
import { IPImageFit } from 'baseComponents/Image/IPImage';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IWizardStep, WizardProgress } from 'components/Wizard/WizardProgress';
import { globalStackTokensGapLarge } from 'globalStyles';
import { CopilotPanelContext } from 'models/copilot/copilotBridge';
import { useCopilotContext } from 'models/copilot/copilotContext';
import { CopilotTrialWelcomeContent } from 'models/copilot/copilotTrialWelcome';
import Language from 'models/language';
import { ISONorm } from 'models/norm';
import Package from 'models/package';
import { DefLanguageCode, OrgLanguageCodes } from 'models/setting';
import { TenantOnboarding } from 'models/tenant';
import UserLanguage from 'models/userLanguage';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiCopilotFinishOnboardingProgress } from 'services/Api/copilotService';
import { apiGetContactPhone } from 'services/Api/hubspotService';
import { apiGetISONorms } from 'services/Api/isoControlService';
import { apiGetLanguages } from 'services/Api/languageService';
import { apiGetPackagesForStore, apiGetPackagesForStoreId } from 'services/Api/packageService';
import { apiSetSetting } from 'services/Api/settingService';
import { apiGetTenantOnboardingState, apiUpdateTenantTrialOnboarding } from 'services/Api/tenantService';
import { apiUpdateUser } from 'services/Api/userService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';
import Config from 'services/Config/configService';
import { SubscriptionTypes } from 'utils/subscription';
import TrialWelcomeAskConfirm from './TrialWelcomeAskConfirm';
import TrialWelcomeFrameworks from './TrialWelcomeFrameworks';
import TrialWelcomeFrameworksNIS2 from './TrialWelcomeFrameworksNIS2';
import TrialWelcomeLanguage from './TrialWelcomeLanguage';
import TrialWelcomeNextSteps from './TrialWelcomeNextSteps';
import TrialWelcomeTimeline from './TrialWelcomeTimeline';

interface ITrialWelcomeProps {}

export const maxStandards = 7;
export const lastStep = 3;

export const imagePropsTrialWelcome: IImageProps = {
  src: `${Config.getImageURL()}/trialwelcome.png`,
  imageFit: IPImageFit.contain,
  width: 72,
  height: 72,
};

const TrialWelcome = (props: ITrialWelcomeProps) => {
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [isLoadingDone, setIsLoadingDone] = useState<boolean>(false);
  const [packages, setPackages] = useState<Package[]>([]);
  const [onboardingState, setOnboardingState] = useState<TenantOnboarding>(new TenantOnboarding());
  const [languages, setLanguages] = useState<Language[]>([]);
  const [selectedLang, setSelectedLang] = useState<Language | undefined>(undefined);
  const [activeStep, setActiveStep] = useState<number>(0);
  const [standards, setStandards] = useState<ISONorm[]>([]);
  const [showProject, setShowProject] = useState<boolean>(false);

  const openProjectDocumentModal = useCallback(() => setShowProject(true), []);

  const copilotContent = useMemo(
    () => new CopilotTrialWelcomeContent(openProjectDocumentModal),
    [openProjectDocumentModal],
  );

  const shouldForceCopilotPanel = activeStep === 3;

  useCopilotContext(appContext.copilotBridge, CopilotPanelContext.TrialWelcome, copilotContent, {
    forceOpen: shouldForceCopilotPanel,
    forcePinned: shouldForceCopilotPanel ? true : undefined,
  });

  //create steps without labels: the onboarding shows progress as a strip of dots, not named steps
  const stepperMaxSteps = lastStep - 1;
  const getStepperSteps = Array.from({ length: stepperMaxSteps + 1 }, (_, i) => i).map(() => '');
  const [stepperSteps] = useState<IWizardStep[]>(
    getStepperSteps.map((label, index) => ({ key: `step-${index}`, label })),
  );

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getOrgLang = (languages: Language[], state: TenantOnboarding): Language => {
    //initially, define the default org language as the user language
    let langCode = appContext.user.language.code;
    if (state.onboardingSteps.languageIsSet === true) {
      //when this step is already completed, get the default language from the settings
      langCode = appContext.globalDataCache.settings.get(DefLanguageCode) as string;
    }

    let language = languages.find((l) => l.code === langCode);
    if (!language) {
      //fallback to the default language
      if (!language) {
        language = new Language();
        language.code = UserLanguage.getFallBack();
        language.languageId = 1;
        language.name = 'English';
      }
    }

    return language;
  };

  const loadData = async () => {
    if (isLoading || isLoadingDone) return;

    try {
      setIsLoading(true);
      appContext.showContentLoader();

      //only managers/admins are allowed to follow the onboarding steps, during the trial or until the first run finished
      if (
        (appContext.user.login.isInTrial() || appContext.user.login.hasUnfinishedOnboarding) &&
        hasUserFeatureGenericManager(appContext)
      ) {
        const accessToken: string = await appContext.getAccessToken(apiRequest.scopes);

        //get the current on-boarding state
        const onboardingState = await apiGetTenantOnboardingState(accessToken);
        setOnboardingState(onboardingState);

        //the server decides at login whether the set-up still hides the main navigation; release it here when the
        //set-up finished since then (another tab, a lost response). The client never hides it on its own: it cannot
        //tell a state without the finished property (never hidden) from one with the value null.
        if (onboardingState.onboardingSteps.finished && appContext.isOnboardingInProgress) {
          appContext.setOnboardingInProgress(false);
        }

        //get all languages
        const langCol = await apiGetLanguages(accessToken);
        setLanguages(langCol.languages);
        //get the default language based on the on-boarding state and user language
        const orgLang = getOrgLang(langCol.languages, onboardingState);
        setSelectedLang(orgLang);

        //load specific packages
        const packages = await apiGetPackagesForStoreId(
          onboardingState.packageIds.map((id) => Number.parseInt(id)),
          accessToken,
        );

        //load all preview packages
        let previewPackages = await apiGetPackagesForStore(false, accessToken);
        previewPackages = previewPackages.filter((p) => p.isPreview);

        setPackages([...packages, ...previewPackages]);

        //load ISO standards
        const standards = await apiGetISONorms(false, accessToken);
        setStandards(standards);

        //get phone from CRM
        const phone = await apiGetContactPhone(accessToken);
        if (phone) {
          onboardingState.onboardingSteps.phone = phone;
        }

        //determine the active step
        if (await getIsRunning()) {
          setIsRunning(true);
          setActiveStep(stepperMaxSteps);
        } else {
          setIsRunning(false);
          let nextStep = onboardingState.onboardingSteps.currentStep || 0;
          if (nextStep > lastStep) {
            nextStep = lastStep;
          }
          //When a new trial is started, currentStep is set to 0 by the back-end.
          //When the currentStep is undefined, this is an existing customer that did not started the on-boarding yet.
          //In this case, we want to skip the on-boarding and start at the overview (last step)
          //where the customer can start the on-boarding (if the trial is active).
          if (nextStep === undefined) {
            nextStep = lastStep;
          }
          setActiveStep(nextStep);
        }
      } else {
        setActiveStep(lastStep);
      }
    } catch (err) {
      appContext.setOnboardingInProgress(false);
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
      setIsLoading(false);
      setIsLoadingDone(true);
    }
  };

  const getIsRunning = async (): Promise<boolean> => {
    const accessToken: string = await appContext.getAccessToken(apiRequest.scopes);
    const result = await apiCopilotFinishOnboardingProgress(accessToken);

    return (result || 0) > 0;
  };

  const updateOnboardingState = async (onboardingState: TenantOnboarding) => {
    try {
      setOnboardingState(onboardingState);
      const accessToken: string = await appContext.getAccessToken(apiRequest.scopes);
      await apiUpdateTenantTrialOnboarding(onboardingState, accessToken);
    } catch (err) {
      appContext.setError(err);
    }
  };

  const updateProjectDocument = (doc: string | undefined) => {
    const newOnboardingState = onboardingState.clone();
    newOnboardingState.onboardingSteps.projectDocument = doc;
    updateOnboardingState(newOnboardingState);
  };

  const onNext = (state: TenantOnboarding) => {
    let newActiveStep = activeStep;
    if (newActiveStep === 1 && !state.onboardingSteps.isCertified() && !state.onboardingSteps.needNewStandards()) {
      //user answered no to both framework questions: skip and go to the overview
      newActiveStep = getStepperSteps.length + 1;
    } else {
      newActiveStep = activeStep + 1;
    }

    const newOnboardingState = state.clone();
    newOnboardingState.onboardingSteps.currentStep = newActiveStep;

    setActiveStep(newActiveStep);
    updateOnboardingState(newOnboardingState);
  };

  const onPrev = (state: TenantOnboarding) => {
    const newActiveStep = activeStep - 1;

    const newOnboardingState = state.clone();
    newOnboardingState.onboardingSteps.currentStep = newActiveStep;

    setActiveStep(newActiveStep);
    updateOnboardingState(newOnboardingState);
  };

  const updatePackageStateInStore = (updatePack: Package | undefined) => {
    if (!updatePack) return;
    const newPackages = [...packages];
    const existingPack = packages.find((p) => p.packageId === updatePack.packageId);
    if (existingPack) {
      existingPack.state = updatePack.state;
      setPackages(newPackages);
    }
  };

  const setUserLanguage = async (newLang: string): Promise<void | undefined> => {
    try {
      if (!UserLanguage.getSupportedLanguages().has(newLang)) {
        newLang = UserLanguage.getFallBack();
      }

      const newLanguage = languages.find((l) => l.code === newLang);
      setSelectedLang(newLanguage);
      if (appContext.user.language.code === newLang) return;

      //update the user language in the application
      newLang = await appContext.changeUserLanguage(newLang);

      //override the default lanauge of the user
      appContext.user.login.userLanguageCode = newLang;

      //update the user preference in the back-end
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiUpdateUser(appContext.user, accessToken);

      //Load standards again to reflect the language change
      const standards = await apiGetISONorms(false, accessToken);
      setStandards(standards);
    } catch (err) {
      appContext.setError(err);
    }
  };

  const updateOrgLang = async (langCode: string) => {
    try {
      //add the selected language to the organizational language when it is not present
      const accessToken: string = await appContext.getAccessToken(apiRequest.scopes);
      const orgLangs = appContext.globalDataCache.settings.get(OrgLanguageCodes) as string[];
      if (!orgLangs.includes(langCode)) {
        orgLangs.push(langCode);
        await apiSetSetting(OrgLanguageCodes, orgLangs, accessToken);
        appContext.globalDataCache.settings.set(OrgLanguageCodes, orgLangs);
      }

      //set the default language setting
      const defLang = appContext.globalDataCache.settings.get(DefLanguageCode) as string;
      if (langCode !== defLang) {
        await apiSetSetting(DefLanguageCode, langCode, accessToken);
        appContext.globalDataCache.settings.set(DefLanguageCode, langCode);
      }
    } catch (err) {
      appContext.setError(err);
    }
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <TrialWelcomeLanguage
            selectedLang={selectedLang}
            languages={languages}
            onboardingState={onboardingState}
            updateOrgLang={updateOrgLang}
            updateUserLang={setUserLanguage}
            onNext={onNext}
          />
        );
      case 1:
        if (appContext.user.tenant.subscriptionType === SubscriptionTypes.NIS2) {
          return (
            <TrialWelcomeFrameworksNIS2
              standards={standards}
              onboardingState={onboardingState}
              onNext={(state) => onNext(state)}
              onPrev={(state) => onPrev(state)}
            />
          );
        } else {
          return (
            <TrialWelcomeFrameworks
              standards={standards}
              onboardingState={onboardingState}
              onNext={(state) => onNext(state)}
              onPrev={(state) => onPrev(state)}
            />
          );
        }
      case 2:
        return (
          <TrialWelcomeAskConfirm
            onboardingState={onboardingState}
            onNext={(state) => onNext(state)}
            onPrev={(state) => onPrev(state)}
            standards={standards}
            isRunning={isRunning}
            selectedLang={selectedLang}
          />
        );
      case 3:
        return (
          <TrialWelcomeNextSteps
            onboardingState={onboardingState}
            updateOnboardingState={updateOnboardingState}
            packages={packages}
            updatePackage={updatePackageStateInStore}
            selectedLang={selectedLang}
          />
        );
      default:
        return null;
    }
  };

  //
  // Main render
  //
  if (isLoading || !isLoadingDone) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack horizontal horizontalAlign="center">
      <IPStack verticalFill tokens={globalStackTokensGapLarge}>
        {activeStep < getStepperSteps.length && (
          <IPStack.Item styles={{ root: { transition: 'top: 0.5s ease' } }}>
            <WizardProgress activeStepKey={`step-${activeStep}`} steps={stepperSteps} />
          </IPStack.Item>
        )}
        <IPStack.Item styles={{ root: { paddingBottom: 20 } }}>{getStepContent(activeStep)}</IPStack.Item>
      </IPStack>
      {showProject && (
        <TrialWelcomeTimeline
          isOpen={showProject}
          onClose={() => setShowProject(false)}
          onboardingState={onboardingState}
          onUpdateProjectDocument={updateProjectDocument}
        />
      )}
    </IPStack>
  );
};

export default TrialWelcome;
